import React, { useEffect, useState } from 'react';
import api from '../../api';

const Photo = ({ p, big }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let u = '';
    api.get(`/wound-photos/${p.id}/image`, { responseType: 'blob' }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [p.id]);
  return (
    <figure className={`border rounded-lg bg-white overflow-hidden ${big ? '' : 'w-56 shrink-0'}`} data-testid={`wound-photo-${p.id}`}>
      <div className={`bg-gray-100 flex items-center justify-center ${big ? 'h-80' : 'h-40'}`}>
        {url ? <img src={url} alt={`${p.woundLocation} on ${p.date}`} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-gray-400">Loading…</span>}
      </div>
      <figcaption className="px-2 py-1.5 text-xs space-y-0.5">
        <p className="font-semibold text-sm">{p.date}</p>
        <p>{p.measurements || 'No measurement'}{p.stage ? ` · Stage ${p.stage}` : ''}</p>
        {(p.woundType || p.drainage) && <p className="text-gray-500">{[p.woundType, p.drainage && `drainage ${p.drainage}`].filter(Boolean).join(' · ')}</p>}
      </figcaption>
    </figure>
  );
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const area = (m) => { const n = (m || '').match(/[\d.]+/g); return n && n.length >= 2 ? Number(n[0]) * Number(n[1]) : null; };

// Wound photos from the Upload Hub, grouped by wound and laid out by date, with first-vs-latest comparison
export const WoundProgressTab = ({ patientId }) => {
  const [photos, setPhotos] = useState(null);
  useEffect(() => { api.get(`/patients/${patientId}/wound-photos`).then((r) => setPhotos(r.data.photos)).catch(() => setPhotos([])); }, [patientId]);
  if (!photos) return <p className="text-gray-500">Loading wound photos…</p>;
  if (!photos.length) return <p className="text-gray-500" data-testid="wounds-empty">No wound photos yet. Upload one in Upload Hub &gt; Wound photo.</p>;
  const label = (p) => { const l = (p.woundLocation || 'Unspecified').trim(); return l.charAt(0).toUpperCase() + l.slice(1).toLowerCase(); };
  const groups = photos.reduce((g, p) => ({ ...g, [label(p)]: [...(g[label(p)] || []), p] }), {});
  return (
    <div className="space-y-8" data-testid="wounds-tab">
      {Object.entries(groups).map(([loc, list]) => {
        const first = list[0]; const last = list[list.length - 1];
        const a1 = area(first.measurements); const a2 = area(last.measurements);
        const change = list.length > 1 && a1 && a2 ? Math.round(((a2 - a1) / a1) * 100) : null;
        return (
          <section key={loc} data-testid={`wound-group-${slug(loc)}`}>
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <h3 className="font-semibold text-lg">{loc}</h3><span className="text-sm text-gray-500">{list.length} photo{list.length === 1 ? '' : 's'}</span>
              {change !== null && <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${change <= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid={`wound-change-${slug(loc)}`}>
                Area {change <= 0 ? 'down' : 'up'} {Math.abs(change)}% since {first.date}</span>}
            </div>
            {list.length > 1 && (
              <div className="grid md:grid-cols-2 gap-3 mb-3" data-testid={`wound-compare-${slug(loc)}`}>
                <div><p className="text-xs font-bold uppercase text-gray-500 mb-1">First</p><Photo p={first} big /></div>
                <div><p className="text-xs font-bold uppercase text-gray-500 mb-1">Latest</p><Photo p={last} big /></div>
              </div>
            )}
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid={`wound-timeline-${slug(loc)}`}>{list.map((p) => <Photo key={p.id} p={p} />)}</div>
          </section>
        );
      })}
      <p className="text-xs text-gray-500">Measurements come from the reviewed Upload Hub entry. Always measure with a ruler in the photo.</p>
    </div>
  );
};
